/**
 * Webhooks. An incoming webhook gives a channel a secret URL that other services can post messages to, under a
 * name of their choosing. (Outgoing webhooks, which send a space's events to other services, are separate.)
 */
import { z } from 'zod';
import { isReservedBotName } from './bots';
import { embedListSchema } from './embeds';
import { LIMITS } from './constants';

export const WEBHOOK_LIMITS = {
  name: 80,
  /** Incoming webhooks per channel, and per space. */
  perChannel: 15,
  perSpace: 100,
  /** The prefix of a webhook's secret, so a leaked one is easy to spot (and is scrubbed from logs). */
  tokenPrefix: 'swh_',
} as const;

const reservedMessage = 'Webhook names cannot contain Spawnwire, System, Official, Staff, Admin or Moderator';

/** The name a webhook posts under. Names that could pass for Spawnwire or its staff are refused, as for bots. */
export const webhookNameSchema = z
  .string()
  .trim()
  .min(1, 'A webhook needs a name')
  .max(WEBHOOK_LIMITS.name)
  .refine((v) => !/[\p{C}]/u.test(v), 'Names cannot contain control characters')
  .refine((v) => !isReservedBotName(v), reservedMessage);

export const webhookCreateSchema = z.object({ name: webhookNameSchema });

/** Rename a webhook, or move it to another channel of the same space. */
export const webhookUpdateSchema = z
  .object({ name: webhookNameSchema, channelId: z.string().regex(/^[0-9a-f-]{36}$/i, 'Invalid id') })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change');

/** What a service sends to post a message. At least text or an embed. */
export const webhookExecuteSchema = z
  .object({
    content: z.string().max(LIMITS.messageLength).optional(),
    /** Post under this name for this message only. */
    username: webhookNameSchema.optional(),
    embeds: embedListSchema.optional(),
  })
  .refine((v) => (v.content ?? '').trim().length > 0 || (v.embeds?.length ?? 0) > 0, 'A message needs text or an embed');

export interface Webhook {
  id: string;
  spaceId: string;
  channelId: string;
  creatorId: string | null;
  name: string;
  createdAt: string;
  /** The secret URL path (`/api/webhooks/<id>/<token>`). Only shown when the webhook is created or its token is reset. */
  url?: string;
}

/** The token inside a webhook URL: `swh_` and 256 random bits. */
export const WEBHOOK_TOKEN_SHAPE = /swh_[A-Za-z0-9_-]{10,}/g;

// ---- Outgoing webhooks -----------------------------------------------------------------------

/**
 * What an outgoing webhook can subscribe to: events of the space itself, in the shapes bots get on the gateway. Nothing
 * about typing, presence, voice, reactions, direct conversations or who voted or is interested, and nothing from a
 * channel that some members can't see.
 */
export const WEBHOOK_EVENTS = [
  'message.created',
  'message.updated',
  'message.deleted',
  'member.joined',
  'member.left',
  'member.kicked',
  'member.banned',
  'member.unbanned',
  'channel.created',
  'channel.updated',
  'channel.deleted',
  'role.created',
  'role.updated',
  'role.deleted',
  'event.created',
  'event.updated',
  'event.deleted',
] as const;
export type WebhookEventName = (typeof WEBHOOK_EVENTS)[number];

export const OUTGOING_LIMITS = {
  perSpace: 10,
  url: 500,
  /** Tries for one delivery, the first included. */
  attempts: 6,
  /** A delivery that fails this many times in a row (after all its tries) turns the webhook off. */
  failuresBeforeDisable: 10,
  /** A request body is at most this big (bytes). */
  bodyBytes: 256 * 1024,
} as const;

/** Where deliveries go: https only, a real host name or public address, no sign-in details in the address. */
export const outgoingUrlSchema = z
  .string()
  .trim()
  .max(OUTGOING_LIMITS.url)
  .regex(/^https:\/\/([a-z0-9][a-z0-9.-]*|\[[0-9a-f:.]+\])(:\d{1,5})?(\/[^\s#]*)?$/i, 'The address must be an https:// link without a password or #fragment');

const eventsSchema = z
  .array(z.enum(WEBHOOK_EVENTS))
  .min(1, 'Pick at least one event')
  .refine((list) => new Set(list).size === list.length, 'The same event is listed twice');

export const outgoingCreateSchema = z.object({ name: webhookNameSchema, url: outgoingUrlSchema, events: eventsSchema });

export const outgoingUpdateSchema = z
  .object({ name: webhookNameSchema, url: outgoingUrlSchema, events: eventsSchema, enabled: z.boolean() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change');

export interface OutgoingWebhook {
  id: string;
  spaceId: string;
  creatorId: string | null;
  name: string;
  url: string;
  events: WebhookEventName[];
  /** Off by hand, or switched off after too many failures (see `disabledReason`). */
  enabled: boolean;
  disabledReason: string | null;
  createdAt: string;
  /** The signing secret. Only shown when the webhook is made or its secret is replaced. */
  secret?: string;
}

export type DeliveryStatus = 'pending' | 'delivered' | 'failed';

/** One try at telling an outgoing webhook about an event, as the log shows it. */
export interface WebhookDelivery {
  id: string;
  event: string;
  status: DeliveryStatus;
  attempts: number;
  lastStatusCode: number | null;
  lastError: string | null;
  createdAt: string;
  deliveredAt: string | null;
}
